import { useState } from "react";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import type { Notification } from "@/components/Notifications/notifications";
import { toTitleCase } from "@/utils/tableConfigs/ActivitiesTableUtilFunctions";

export type CalendarFilterType = "on" | "before" | "after" | "between" | null;

export const useCalendarFilter = () => {
  const DEFAULT_TEXT = "Select dates";

  const [value, setValue] = useState<CalendarFilterType>("on");
  const [selectedDates, setSelectedDates] = useState<
    Dayjs | [Dayjs, Dayjs] | null
  >(null);
  const [appliedDates, setAppliedDates] = useState<
    Dayjs | [Dayjs, Dayjs] | null
  >(null);
  const [appliedValue, setAppliedValue] = useState<CalendarFilterType>(null);
  const [headerText, setHeaderText] = useState(DEFAULT_TEXT);
  const [areDatesSelected, setAreDatesSelected] = useState(false);
  const [open, setOpen] = useState(false);

  const handleClickTile = () => {
    setOpen(true);
  };

  const onChangeRadioButton = (e: any) => {
    const newValue = e.target.value;
    setValue(newValue);
    setSelectedDates(null);
  };

  const onChangeDates = (dates: Dayjs | [Dayjs, Dayjs] | null) => {
    setSelectedDates(dates);
  };

  const onClose = () => {
    setOpen(false);
  };

  const onClear = () => {
    setSelectedDates(null);
    setAppliedDates(null);
    setAppliedValue(null);
    setValue(null);
    setHeaderText(DEFAULT_TEXT);
    setAreDatesSelected(false);
  };

  const onApply = () => {
    setOpen(false);
    setAppliedDates(selectedDates);
    setAppliedValue(value);

    if (selectedDates !== null) {
      setHeaderText(`${toTitleCase(value || "")}:`);
      setAreDatesSelected(true);
    } else {
      setHeaderText(DEFAULT_TEXT);
      setAreDatesSelected(false);
    }
  };

  const filterNotifications = (
    notifications: Notification[],
  ): Notification[] => {
    if (!appliedValue || !appliedDates) {
      return notifications;
    }

    return notifications.filter((notification: Notification) => {
      const notificationDate = dayjs(notification.notification_data?.at);

      switch (appliedValue) {
        case "on":
          if (appliedDates && !Array.isArray(appliedDates)) {
            return notificationDate.isSame(appliedDates, "day");
          }
          return false;
        case "before":
          if (appliedDates && !Array.isArray(appliedDates)) {
            return notificationDate.isBefore(appliedDates, "day");
          }
          return false;
        case "after":
          if (appliedDates && !Array.isArray(appliedDates)) {
            return notificationDate.isAfter(appliedDates, "day");
          }
          return false;
        case "between":
          if (
            appliedDates &&
            Array.isArray(appliedDates) &&
            appliedDates[0] &&
            appliedDates[1]
          ) {
            return (
              (notificationDate.isAfter(appliedDates[0], "day") ||
                notificationDate.isSame(appliedDates[0], "day")) &&
              (notificationDate.isBefore(appliedDates[1], "day") ||
                notificationDate.isSame(appliedDates[1], "day"))
            );
          }
          return false;
        default:
          return true;
      }
    });
  };

  return {
    selectedDates,
    value,
    appliedDates,
    appliedValue,
    headerText,
    areDatesSelected,
    open,

    handleClickTile,
    onChangeRadioButton,
    onChangeDates,
    onClose,
    onClear,
    onApply,
    filterNotifications,
  };
};
